import { useQuery } from "@tanstack/react-query";
import type { AnyMediaProvider, MediaProviderAdapter } from "@vidstack/react";
import { isVideoProvider } from "@vidstack/react";
import { isDASHProvider, isHLSProvider } from "@vidstack/react";

type AllowedExtensions = "mpd" | "m3u8";

const mimeTypeByExtension = {
  mpd: "application/dash+xml",
  m3u8: "application/x-mpegURL",
} as const;

type MediaSource = {
  poster?: string;
  src: string;
  type: string;
};

const CLOUDFLARE_STREAM_IFRAME_HOSTS = new Set([
  "iframe.cloudflarestream.com",
  "iframe.videodelivery.net",
]);
const LIVEPEER_IFRAME_HOSTS = new Set(["lvpr.tv", "www.lvpr.tv"]);
const LIVEPEER_PLAYBACK_API_URL = "https://livepeer.studio/api/playback";

type LivepeerPlaybackInfo = {
  meta?: {
    source?: LivepeerPlaybackSource[];
  };
};

type LivepeerPlaybackSource = {
  type?: string;
  url?: string;
};

const getVideoIdFromUrl = (url: string) => {
  try {
    const parsedUrl = new URL(url);

    if (!CLOUDFLARE_STREAM_IFRAME_HOSTS.has(parsedUrl.hostname)) {
      return null;
    }

    return parsedUrl.pathname.split("/").find(Boolean) ?? null;
  } catch {
    // Takes ID from legacy values like
    // https://iframe.videodelivery.net/6e8f37b8bb024effec5d195de924a598
    const match =
      /iframe\.(?:cloudflarestream\.com|videodelivery\.net)\/([^/?#]+)/.exec(
        url,
      );

    return match?.[1] ?? null;
  }
};

export const getMediaUrl = (
  url: string,
  extension: AllowedExtensions = "mpd",
) => {
  const videoId = getVideoIdFromUrl(url);

  if (videoId) {
    return `https://customer-${
      process.env.NEXT_PUBLIC_CLOUDFLARE_CUSTOMER_ID
    }.cloudflarestream.com/${videoId}/manifest/video.${extension}`;
  }
  return url;
};

const getUrlExtension = (url: string): AllowedExtensions | null => {
  try {
    const pathname = new URL(url).pathname.toLowerCase();

    if (pathname.endsWith(".m3u8")) return "m3u8";
    if (pathname.endsWith(".mpd")) return "mpd";

    return null;
  } catch {
    const match = /\.([a-z0-9]+)(?:$|[?#])/.exec(url.toLowerCase());
    const extension = match?.[1];

    return extension === "m3u8" || extension === "mpd" ? extension : null;
  }
};

export const getMediaSourceFromUrl = (url: string): MediaSource => {
  const extension = getUrlExtension(url);

  if (extension) {
    return {
      src: url,
      type: mimeTypeByExtension[extension],
    };
  }

  return {
    src: url,
    type: "video/mp4",
  };
};

export const getLivepeerPlaybackIdFromUrl = (url: string) => {
  try {
    const parsedUrl = new URL(url);

    if (!LIVEPEER_IFRAME_HOSTS.has(parsedUrl.hostname)) {
      return null;
    }

    return (
      parsedUrl.searchParams.get("v") ??
      parsedUrl.searchParams.get("playbackId")
    );
  } catch {
    const match = /lvpr\.tv\/?\?[^#]*(?:v|playbackId)=([^&#]+)/.exec(url);

    return match?.[1] ? decodeURIComponent(match[1]) : null;
  }
};

const getLivepeerDirectUrlFromEmbedUrl = (url: string) => {
  try {
    const parsedUrl = new URL(url);

    if (!LIVEPEER_IFRAME_HOSTS.has(parsedUrl.hostname)) {
      return null;
    }

    return parsedUrl.searchParams.get("url");
  } catch {
    const match = /lvpr\.tv\/?\?[^#]*url=([^&#]+)/.exec(url);

    return match?.[1] ? decodeURIComponent(match[1]) : null;
  }
};

const isLivepeerHlsSource = (source: LivepeerPlaybackSource) => {
  return (
    !!source.url &&
    (getUrlExtension(source.url) === "m3u8" ||
      source.type?.toLowerCase().includes("mpegurl"))
  );
};

const isLivepeerVideoSource = (source: LivepeerPlaybackSource) => {
  return !!source.url && !!source.type?.toLowerCase().includes("video/");
};

export const resolveLivepeerPlaybackSource = async (url: string) => {
  const directUrl = getLivepeerDirectUrlFromEmbedUrl(url);

  if (directUrl) {
    return getMediaSourceFromUrl(directUrl);
  }

  const playbackId = getLivepeerPlaybackIdFromUrl(url);

  if (!playbackId) {
    return null;
  }

  const response = await fetch(
    `${LIVEPEER_PLAYBACK_API_URL}/${encodeURIComponent(playbackId)}`,
  );

  if (!response.ok) {
    throw new Error("Failed to resolve Livepeer playback info");
  }

  const playbackInfo = (await response.json()) as LivepeerPlaybackInfo;
  const sources = playbackInfo.meta?.source ?? [];
  const source =
    sources.find(isLivepeerHlsSource) ?? sources.find(isLivepeerVideoSource);

  if (!source?.url) {
    throw new Error("Livepeer playback info did not include a video source");
  }

  return getMediaSourceFromUrl(source.url);
};

const getPosterUrl = (url: string) => {
  const videoId = getVideoIdFromUrl(url);

  if (videoId) {
    return `https://customer-${process.env.NEXT_PUBLIC_CLOUDFLARE_CUSTOMER_ID}.cloudflarestream.com/${videoId}/thumbnails/thumbnail.jpg?time=0.1s&height=600`;
  }
  return url;
};

export const configureMediaProvider = (
  provider: MediaProviderAdapter | null,
) => {
  if (isHLSProvider(provider)) {
    provider.library = () => import("hls.js");
  } else if (isDASHProvider(provider)) {
    provider.library = () => import("dashjs");
  }
};

export const sharedPlayerProps = {
  onProviderChange: configureMediaProvider,
};

type UseMediaSourceProps = {
  url: string;
  preProcessManifest?: (manifest: string, manifestUrl: string) => string;
  extension?: "mpd" | "m3u8";
};

/**
 * We're prefetching the manifest for faster start-up. We're doing this way for multiple reasons:
 *
 * - Cloudflare is sending it with low cache-headers.
 * - DASH.js is using XHR which wouldn't take fetch cache into account.
 * - This way cache is persisted across re-renders (scrolling up-down in the feed)
 */
export const useMediaSource = ({
  url,
  preProcessManifest = (x) => x,
  extension = "mpd",
}: UseMediaSourceProps) => {
  const { data } = useQuery({
    queryKey: ["media-source", url, extension],
    queryFn: async () => {
      const livepeerSource = await resolveLivepeerPlaybackSource(url);

      if (livepeerSource) {
        return livepeerSource;
      }

      if (!getVideoIdFromUrl(url)) {
        return getMediaSourceFromUrl(url);
      }

      const manifestUrl = getMediaUrl(url, extension);

      const blob = await fetch(manifestUrl)
        .then((response) => response.text())
        .then((data) => preProcessManifest(data, manifestUrl))
        .then((data) =>
          URL.createObjectURL(
            new Blob([data], { type: mimeTypeByExtension[extension] }),
          ),
        );

      return {
        src: blob,
        type: mimeTypeByExtension[extension],
        poster: getPosterUrl(url),
      };
    },
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });

  // Uncomment this to test directly the remote manifest
  /*return {
    src: getMediaUrl(url, extension),
    type: mimeTypeByExtension[extension],
  };*/

  return data;
};

export const preProcessDashManifest = (
  xmlString: string,
  manifestUrl: string,
) => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlString, "text/xml");
  // Remove video track from manifest
  // We can enable this once vidstack resolved this issue: https://github.com/vidstack/player/issues/1454
  // xmlDoc.querySelector("AdaptationSet[mimeType='video/mp4']")?.remove();
  // Replace relative URLS with absolute URLS
  xmlDoc.querySelectorAll("[initialization][media]")?.forEach((el) => {
    el.setAttribute(
      "initialization",
      new URL(el.getAttribute("initialization")!, manifestUrl).href,
    );
    el.setAttribute(
      "media",
      new URL(el.getAttribute("media")!, manifestUrl).href,
    );
  });
  return new XMLSerializer().serializeToString(xmlDoc);
};

export const getMediaElementFromProvider = (
  provider?: MediaProviderAdapter | AnyMediaProvider | null,
) => {
  switch (true) {
    case isHLSProvider(provider):
      return provider?.media;
    case isDASHProvider(provider):
      return provider?.video;
    case isVideoProvider(provider):
      return provider.media;
    default:
      return null;
  }
};
